import React, { Component } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { FormGroup, Label, Input } from 'reactstrap';

import { AlertButton, PrimaryButton } from 'ui/Components';
import { ptToUtcDateString, utcToPtDateString } from 'lib/date';

import styles from './Publish.css';

/**
 * This the the screen to capture user inputs to publish a page or component.
 * This shows date and time fields to schedule the publish or a button to publish now.
 * This component calls onPublish callback with user inputs in form of parameters.
 * publishNow: {boolean} and startDate: {DateTime}
 * @param {object} props - the props passed to this component
 * @returns {React.Component} the PublishComponent component
 */
export default class Publish extends Component {
  static propTypes = {
    showOnlyPublishNowButton: propTypes.bool,
    message: propTypes.string,
    onPublish: propTypes.func.isRequired,
    publishDate: propTypes.string,
    publishNowButtonText: propTypes.string,
    scheduleButtonText: propTypes.string,
    isTestContent: propTypes.bool,
    routes: propTypes.array
  };

  static defaultProps = {
    showOnlyPublishNowButton: false,
    message: undefined,
    publishDate: undefined,
    publishNowButtonText: 'Publish Live Now',
    scheduleButtonText: 'Schedule Publish',
    isTestContent: false,
    routes: []
  };

  constructor(props) {
    super(props);
    this.state = {
      date: props.publishDate ? utcToPtDateString(props.publishDate, 'YYYY-MM-DD') : undefined,
      time: props.publishDate ? utcToPtDateString(props.publishDate, 'HH:mm') : undefined,
      isTestContent: props.isTestContent,
      routes: props.routes
    };

    this.onDateChange = this.onDateChange.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
    this.onTestContentChange = this.onTestContentChange.bind(this);
  }

  /**
   * sets is selected date value to current component's state
   * @param {object} e - date input change event
   */
  onDateChange(e) {
    this.setState({
      date: e.target.value
    });
  }

  /**
   * sets is selected time value to current component's state
   * @param {object} e - time input change event
   */
  onTimeChange(e) {
    this.setState({
      time: e.target.value
    });
  }

  onTestContentChange(e) {
    this.setState({
      isTestContent: e.target.checked
    });
  }

  /**
   * returns selecgted date time in UTC format.
   * @returns {string} selected datetime in UTC format
   */
  getScheduleDateTime() {
    const { date, time } = this.state;
    const strDateTime = `${date} ${time}`;
    if (new Date(strDateTime).toString() === 'Invalid Date') {
      return null;
    }
    return ptToUtcDateString(strDateTime);
  }

  /**
   * renders publish Live now button
   * @returns {Component} publish live now button
   */
  renderPublishNowButton() {
    const { onPublish, publishNowButtonText } = this.props;
    const { isTestContent } = this.state;
    return (
      <AlertButton
        className="mr-3"
        disabled={isTestContent}
        onClick={() => onPublish(true, null, false)}
        data-test-id="publish-publishLiveNowButton"
      >
        {publishNowButtonText}
      </AlertButton>
    );
  }

  /**
   * renders a list of routes that will be overritten on publish
   * @returns {Component} Routes content
   */
  renderRoutesList() {
    const { routes } = this.state;
    const routeString = routes.length > 1 ? 'routes' : 'route';
    const RouteListItems = routes.map(route => (
      <li className={styles.routeBullets}>{route}</li>
    ));

    return (
      <div data-test-id="route-list-items">
        <div className={styles.routeMessage}>
          {`This action will overwrite the following ${routeString}:`}
          <ul>{RouteListItems}</ul>
        </div>
      </div>
    );
  }

  /**
   * renders contents for WCM Publish
   * @returns {Component} WCM Publish Contents
   */
  renderWCMPublish() {
    const { date, isTestContent, time } = this.state;

    const { onPublish, scheduleButtonText } = this.props;

    return (
      <div
        className={classnames(
          styles.wrapper,
          'd-flex align-items-center justify-content-center'
        )}
      >
        <div className={styles.schedulePublishWrapper}>
          <div className={classnames(styles.schedulePublish, 'pb-5')}>
            <div className="pb-3">
              <Label for="publishDate">
                <strong>Date</strong>
              </Label>
              <Input
                type="date"
                name="date"
                id="publishDate"
                placeholder="Date"
                value={date}
                onChange={this.onDateChange}
                data-test-id="publishDate"
              />
            </div>
            <div className="pb-4">
              <Label for="publishTime">
                <strong>Time</strong>
              </Label>
              <Input
                type="time"
                name="time"
                id="publishTime"
                placeholder="Time"
                value={time}
                onChange={this.onTimeChange}
                data-test-id="publishTime"
              />
            </div>
            <div className={styles.schedulePublishButton}>
              <PrimaryButton
                onClick={() => onPublish(false, this.getScheduleDateTime(), isTestContent)}
                disabled={!this.getScheduleDateTime()}
                data-test-id="publish-schedulePublish"
              >
                {scheduleButtonText}
              </PrimaryButton>
            </div>
          </div>
          <div className="pl-4 pb-4">
            <FormGroup check inline>
              <Label>
                <Input
                  type="checkbox"
                  checked={isTestContent}
                  data-test-id="publish-testContentCheckbox"
                  onChange={this.onTestContentChange}
                />
                Test Content
              </Label>
            </FormGroup>
          </div>
        </div>
        <div className={styles.or}>or</div>
        <div className={styles.publishNow}>
          {this.renderPublishNowButton()}
          {!isTestContent && this.renderRoutesList()}
        </div>
      </div>
    );
  }

  render() {
    const { showOnlyPublishNowButton, message } = this.props;
    return (
      <div>
        <div className="mb-3">{message}</div>
        {showOnlyPublishNowButton ? this.renderPublishNowButton() : this.renderWCMPublish()}
      </div>
    );
  }
}
